import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  FiUsers, 
  FiBarChart2, 
  FiActivity,
  FiTrendingUp,
  FiCheckCircle,
  FiClock,
  FiMusic,
  FiShield
} from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import AdminSidebar from './AdminSidebar';
import DashboardHeader from './DashboardHeader';
import { useSidebar } from '../contexts/SidebarContext';
import { authAPI, adminStatsAPI, adminPlatformSettingsAPI } from '../services/api';
import './Dashboard.css';

interface AdminDashboardProps {
  onLogout?: () => void;
  userEmail?: string;
}

const AdminDashboard: React.FC<AdminDashboardProps> = ({ onLogout, userEmail }) => {
  const { isCollapsed } = useSidebar();
  const navigate = useNavigate();
  const [userInfo, setUserInfo] = useState({
    email: userEmail || '',
    organization: 'NAATI',
    first_name: ''
  });

  const [stats, setStats] = useState({
    totalUsers: 0,
    activeUsers: 0,
    totalTests: 0,
    completedTests: 0,
    pendingTests: 0,
    totalSessions: 0
  });

  const [allowUsernameSignup, setAllowUsernameSignup] = useState(false);
  const [platformSettingsLoading, setPlatformSettingsLoading] = useState(true);
  const [platformSettingsSaving, setPlatformSettingsSaving] = useState(false);
  const [platformSettingsMessage, setPlatformSettingsMessage] = useState<string | null>(null);

  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        const profile = await authAPI.getProfile();
        setUserInfo({
          email: profile.email || userEmail || '',
          organization: profile.organization || 'NAATI',
          first_name: profile.first_name || ''
        });
      } catch (error) {
        console.error('Error fetching user profile:', error);
      }
    };
    fetchUserProfile();

    const fetchStats = async () => {
      try {
        const data = await adminStatsAPI.getStats();
        setStats({
          totalUsers: data.totalUsers,
          activeUsers: data.activeUsers,
          totalTests: data.totalTests,
          completedTests: data.completedTests,
          pendingTests: data.pendingTests,
          totalSessions: data.totalSessions
        });
      } catch (err) {
        console.error('Error fetching admin stats:', err);
      }
    };
    fetchStats();
  }, [userEmail]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setPlatformSettingsLoading(true);
      try {
        const data = await adminPlatformSettingsAPI.get();
        if (!cancelled) {
          setAllowUsernameSignup(Boolean(data.allow_username_signup));
        }
      } catch {
        if (!cancelled) setPlatformSettingsMessage('Could not load platform settings.');
      } finally {
        if (!cancelled) setPlatformSettingsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleToggleUsernameSignup = async () => {
    setPlatformSettingsSaving(true);
    setPlatformSettingsMessage(null);
    try {
      const next = !allowUsernameSignup;
      const data = await adminPlatformSettingsAPI.patch({ allow_username_signup: next });
      setAllowUsernameSignup(Boolean(data.allow_username_signup));
      setPlatformSettingsMessage(
        data.allow_username_signup
          ? 'Public sign-up now uses Username (no email). Existing users still sign in with email.'
          : 'Public sign-up now uses Email again.'
      );
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Update failed';
      setPlatformSettingsMessage(msg);
    } finally {
      setPlatformSettingsSaving(false);
    }
  };

  const handleLogout = () => {
    if (onLogout) {
      onLogout();
    }
  };

  const statCards = [
    { label: 'Total Users', value: stats.totalUsers.toLocaleString(), icon: FiUsers, color: '#E63946' },
    { label: 'Active Users', value: stats.activeUsers.toLocaleString(), icon: FiActivity, color: '#28a745' },
    { label: 'Total Tests', value: stats.totalTests.toLocaleString(), icon: FiBarChart2, color: '#17a2b8' },
    { label: 'Completed Tests', value: stats.completedTests.toLocaleString(), icon: FiCheckCircle, color: '#28a745' },
    { label: 'Pending Tests', value: stats.pendingTests.toLocaleString(), icon: FiClock, color: '#ffc107' },
    { label: 'Total Sessions', value: stats.totalSessions.toLocaleString(), icon: FiTrendingUp, color: '#6c757d' }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: [0.4, 0, 0.2, 1] as const
      }
    }
  };

  return (
    <div className={`dashboard ${isCollapsed ? 'sidebar-collapsed' : ''}`}>
      <AdminSidebar />
      
      <div className="dashboard-main">
        <DashboardHeader
          userEmail={userInfo.email}
          organization={userInfo.organization}
          onLogout={handleLogout}
        />
        
        <div className="dashboard-content">
          <div className="dashboard-content-inner">
            <motion.div
              className="dashboard-welcome"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div>
                <h1 className="dashboard-title">
                  Welcome Back{userInfo.first_name ? `, ${userInfo.first_name}` : ''}
                </h1>
                <p className="dashboard-subtitle">Site Statistics and Overview</p>
              </div>
            </motion.div>

            <motion.div
              className="dashboard-stats"
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              {statCards.map((stat, index) => {
                const IconComponent = stat.icon as React.ComponentType<{ size?: number; color?: string }>;
                return (
                  <motion.div
                    key={index}
                    className="stat-card"
                    variants={itemVariants}
                    whileHover={{ scale: 1.05, y: -5 }}
                    transition={{ duration: 0.2 }}
                  >
                    <div className="stat-icon" style={{ color: stat.color }}>
                      <IconComponent size={28} />
                    </div>
                    <div className="stat-info">
                      <div className="stat-value">{stat.value}</div>
                      <div className="stat-label">{stat.label}</div>
                    </div>
                  </motion.div>
                );
              })}
            </motion.div>

            {/* Sign-up mode (mirrors Django Admin → Platform settings) */}
            <motion.div
              className="quick-actions"
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              style={{ marginBottom: '1.5rem' }}
            >
              <h2 className="section-title">Authentication</h2>
              <div
                className="action-card"
                style={{
                  maxWidth: 560,
                  cursor: platformSettingsLoading || platformSettingsSaving ? 'wait' : 'default',
                  padding: '1.25rem 1.5rem',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem' }}>
                  <div className="action-icon" style={{ backgroundColor: 'rgba(230, 57, 70, 0.12)', color: '#E63946' }}>
                    {React.createElement(FiShield as React.ComponentType<{ size?: number }>, { size: 28 })}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div className="action-title">Sign up with username (fallback)</div>
                    <p className="action-description" style={{ marginBottom: '0.75rem' }}>
                      When enabled, the public <strong>Sign up</strong> page asks for a <strong>username</strong> instead
                      of email (no inbox required). Users who already registered with email keep signing in with email.
                      You can also change this in Django Admin → <em>Platform settings</em>.
                    </p>
                    <label
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        cursor: platformSettingsLoading || platformSettingsSaving ? 'not-allowed' : 'pointer',
                        fontWeight: 600,
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={allowUsernameSignup}
                        disabled={platformSettingsLoading || platformSettingsSaving}
                        onChange={() => {
                          void handleToggleUsernameSignup();
                        }}
                      />
                      Allow username-based sign-up
                    </label>
                    {platformSettingsMessage && (
                      <p style={{ marginTop: '0.75rem', fontSize: '0.85rem', color: '#aaa' }}>
                        {platformSettingsMessage}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Quick Actions */}
            <motion.div
              className="quick-actions"
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              <h2 className="section-title">Quick Actions</h2>
              <div className="actions-grid">
                <motion.div
                  className="action-card"
                  variants={itemVariants}
                  whileHover={{ scale: 1.03, y: -3 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => navigate('/admin/materials')}
                >
                  <div className="action-icon" style={{ backgroundColor: '#E6394620', color: '#E63946' }}>
                    {React.createElement(FiMusic as React.ComponentType<{ size?: number }>, { size: 32 })}
                  </div>
                  <div className="action-title">Material Management</div>
                  <div className="action-description">Upload and manage audio materials</div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;

